import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { FiMoreVertical, FiVolume2 } from "react-icons/fi";
import { DeletePhonemeDocument, GetPhonemesGroupedDocument } from "../../../generated/graphql";
import { EditPhonemeModal } from "./EditPhonemeModal";
import { VoicesModal } from "./VoicesModal";

/**
 * seed data is using standard 44 English phonemes
 * Based on: https://magoosh.com/english-speaking/44-phonemes-in-english-and-other-sound-blends/
 */

interface PhonemeGroup {
  id: string;
  phoneme: string;
  phonemeType: string;
  tags: string[];
  audioCount: number;
}

interface PhonemeCardProps {
  group: PhonemeGroup;
}

export function PhonemeCard({ group }: PhonemeCardProps) {
  const [showVoicesModal, setShowVoicesModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const [deletePhoneme, { loading: deleting }] = useMutation(DeletePhonemeDocument, {
    // phoneme type null in variables, to match apollo normalization with the root query ( avoid re render )
    refetchQueries: [{ query: GetPhonemesGroupedDocument, variables: { phonemeType: null } }],
    onCompleted: () => {
      toast.success("Phoneme moved to trash");
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const handleCardClick = (e: React.MouseEvent) => {
    // don't open voices modal if clicking the menu button
    const target = e.target as HTMLElement;
    if (target.closest("[data-menu-button]")) {
      return;
    }
    setShowVoicesModal(true);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowEditModal(true);
  };

  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);

    if (!confirm(`Move "${group.phoneme}" to trash? This will also trash all audio files.`)) {
      return;
    }

    try {
      await deletePhoneme({
        variables: { id: group.id },
      });
    } catch (error) {
      console.error("Delete phoneme failed:", error);
    }
  };

  return (
    <>
      <div className="relative h-full">
        <button
          type="button"
          onClick={handleCardClick}
          className="group flex w-full h-full flex-col items-center justify-between rounded-lg border-2 border-gray-200 bg-white p-4 transition-all hover:border-blue-500 hover:shadow-md"
        >
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600 group-hover:bg-blue-600 group-hover:text-white">
            <FiVolume2 size={24} />
          </div>
          <div className="text-2xl font-bold text-gray-900">{group.phoneme}</div>
          <div className="mt-2 text-sm text-gray-500">
            {group.audioCount} {group.audioCount === 1 ? "voice" : "voices"}
          </div>
          {group.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1 justify-center">
              {group.tags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                >
                  {tag}
                </span>
              ))}
              {group.tags.length > 2 && (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  +{group.tags.length - 2}
                </span>
              )}
            </div>
          )}
        </button>

        {/* Three-dot menu button */}
        <button
          type="button"
          data-menu-button
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="absolute right-2 top-2 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <FiMoreVertical size={16} />
        </button>

        {/* Dropdown menu */}
        {showMenu && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
            <div className="absolute right-2 top-10 z-20 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
              <div className="py-1">
                <button
                  type="button"
                  onClick={handleEditClick}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  disabled={deleting}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="block w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                  disabled={deleting}
                >
                  {deleting ? "Moving to trash..." : "Move to Trash"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showVoicesModal && (
        <VoicesModal
          phonemeId={group.id}
          phoneme={group.phoneme}
          onClose={() => setShowVoicesModal(false)}
        />
      )}

      {showEditModal && (
        <EditPhonemeModal
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
          }}
          phonemeData={{
            id: group.id,
            phoneme: group.phoneme,
            phonemeType: group.phonemeType,
            tags: group.tags,
          }}
        />
      )}
    </>
  );
}
